import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, Trophy, Timer } from "lucide-react";

/**
 * Slice 2+3 — contextual game overlay (viewer + host). Bottom card above
 * chat; disappears when no game is running. Poll: live % bars. Trivia:
 * countdown, one locked answer, reveal shows correct + winner count.
 */
export default function LiveGameCard({ game, isHost, onDismiss, onRevealed }) {
  const [mine, setMine] = useState(game?.my_answer || null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  // Reset the locked answer only when a NEW game arrives; re-syncing on every
  // poll would clobber the optimistic local answer with a stale server echo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setMine(game?.my_answer || null), [game?.id]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const secondsLeft = useMemo(() => {
    if (!game?.closes_at) return 0;
    return Math.max(0, Math.ceil((new Date(game.closes_at).getTime() - now) / 1000));
  }, [game?.closes_at, now]);

  if (!game) return null;
  const revealed = game.status === "revealed";
  const locked = revealed || secondsLeft <= 0;
  const totalVotes = (game.options || []).reduce((s, o) => s + (o.votes || 0), 0);

  const answer = async (key) => {
    if (mine || locked || busy || isHost) return;
    setBusy(true);
    setMine(key);
    try {
      await api.post(`/games/${game.id}/answer`, { option_key: key });
    } catch {
      /* duplicate/closed — keep optimistic selection */
    }
    setBusy(false);
  };

  const reveal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/games/${game.id}/reveal`);
      onRevealed?.();
    } catch { /* noop */ }
    setBusy(false);
  };

  const showBars = game.type === "poll" || revealed;

  return (
    <div
      data-testid="live-game-card"
      className="pointer-events-auto mx-3 mb-2 rounded-2xl bg-black/70 backdrop-blur-xl border border-white/10 p-3 text-white"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-yellow-300 flex items-center gap-1.5">
          {game.type === "trivia" ? <Trophy size={12} /> : null}
          {game.type === "trivia" ? "Trivia" : "Poll"}
        </span>
        <span className="flex items-center gap-2">
          {!revealed && (
            <span data-testid="game-countdown" className="text-xs text-white/80 flex items-center gap-1 tabular-nums">
              <Timer size={12} /> {secondsLeft}s
            </span>
          )}
          {revealed && onDismiss && (
            <button type="button" data-testid="game-dismiss" onClick={onDismiss} className="text-zinc-400 hover:text-white">
              <X size={14} />
            </button>
          )}
        </span>
      </div>

      <div data-testid="game-question" className="text-sm font-semibold mb-2">{game.question}</div>

      <div className="space-y-1.5">
        {(game.options || []).map((o) => {
          const pct = totalVotes ? Math.round((o.votes / totalVotes) * 100) : 0;
          const isMine = mine === o.key;
          const isCorrect = revealed && game.correct_key === o.key;
          return (
            <button
              key={o.key}
              type="button"
              data-testid={`game-answer-${o.key}`}
              onClick={() => answer(o.key)}
              disabled={locked || !!mine || isHost}
              className={`relative w-full text-left px-3 py-2 rounded-lg border overflow-hidden text-sm transition ${
                isCorrect
                  ? "border-emerald-400 text-emerald-300"
                  : isMine
                    ? "border-yellow-400 text-yellow-300"
                    : "border-white/10 text-white"
              } ${locked || mine || isHost ? "" : "hover:border-white/30"}`}
            >
              {showBars && (
                <span
                  className="absolute inset-y-0 left-0 bg-white/10 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              )}
              <span className="relative flex items-center justify-between">
                <span>{o.label}{isMine ? " · you" : ""}{isCorrect ? " ✓" : ""}</span>
                {showBars && <span className="tabular-nums text-xs text-white/70">{pct}%</span>}
              </span>
            </button>
          );
        })}
      </div>

      {isHost && !revealed && (
        <button
          type="button"
          data-testid="game-reveal-btn"
          onClick={reveal}
          disabled={busy}
          className="mt-2 w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-bold py-2 rounded-full text-xs uppercase tracking-widest"
        >
          {game.type === "trivia" ? "Reveal answer" : "End poll"}
        </button>
      )}
      {revealed && game.type === "trivia" && (
        <div data-testid="game-result-note" className="mt-2 text-xs text-white/70 text-center">
          Correct answer highlighted — points awarded to fastest correct answers.
        </div>
      )}
    </div>
  );
}
